import { faBook, faGlasses, faPenToSquare } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Paginator from "components/Dashboards/General/Pagination/Paginator"
import { ReactNode, useEffect, useState } from "react"
import { Button, Col, Form, Modal, Row } from "react-bootstrap"
import { BeatLoader } from "react-spinners"
import { toast } from "react-toastify"
import { getAssignments } from "shared/routes/teacher"
import { AssignmentResponse, GradingPeriod } from "shared/types/teacherTypes"
import styles from "../../General/general.module.scss"
import ToolTip from "components/General/ToolTip"

interface Props {
    show: boolean
    onHide: () => void
    onSubmit: (periodName: string, assignmentIds: string[], classId: string) => Promise<void>
    period?: GradingPeriod
    type: "create" | "edit" | "view"
    classId: string
}

const GradingPeriodForm = ({ show, onHide, onSubmit, period, type, classId } : Props) => {
    const [assignments, setAssignments] = useState<AssignmentResponse[] | null>(null)
    const [selectedAssignments, setSelectedAssignments] = useState<string[]>([])
    const [periodName, setPeriodName] = useState<string>("")
    const [submitting, setSubmitting] = useState<boolean>(false)

    useEffect(() => {
        if (!period) return
        setPeriodName(period.name)
    }, [period])

    useEffect(() => {
        if (!show) return
        const selectedPeriodsIds = (type === "view" && period) ? [period.id] : []

        getAssignments({ classID: classId, selectedPeriodsIds: selectedPeriodsIds })
        .then(assignments => {
            const assignmentsWithDates = assignments.filter(a => a.dueDate != null && (type === "create" ? !a.periodId : true))
            setAssignments(assignmentsWithDates)

            if (type === "edit" && period) {
                setSelectedAssignments(period.assignments.map(a => a.id))
            }
        })
        .catch(() => {
            toast.error("Failed to load assignments.")
        })
    }, [show, classId, period, type])

    const selectDate = (assignment: AssignmentResponse) => {
        if (type === "view") return
        if (selectedAssignments.includes(assignment.id)) {
            setSelectedAssignments(prev => prev.filter(id => id !== assignment.id))
        } else {
            setSelectedAssignments(prev => [...prev, assignment.id])
        }
    }

    const renderPeriodItem = (a: AssignmentResponse, index: number) : ReactNode => {
        const topicType = a.lessonVariety === "Topic" && !!a.topicId
        const lessonType = a.lessonVariety === "Guided" && !!a.lessonId
        const assessmentType = a.lessonVariety === "Guided" && !a.lessonId && !!a.assessmentId
        const studentSpecificAssignment = a?.students?.length > 0

        return (
            <li 
                role={type === "view" ? "" : "button"} 
                className={`list-group-item ${selectedAssignments.includes(a.id) ? 'bg-primary text-white' : ''}`} 
                key={index + a.id}
                onClick={() => selectDate(a)}>
                <ToolTip title={studentSpecificAssignment ? "Student Specific Assignment" : "Class Assignment"}>
                    <Row>
                        <Col xs={4} className="text-start">
                            <i className="fa-regular fa-calendar me-1" /> 
                            {a.dueDate ? new Date(a.dueDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit" }) : ""}
                        </Col>
                        <Col xs={8} lg={6} className="text-start">{a.title}</Col>
                        <Col xs={0} lg={2} className="text-start d-none d-lg-block">
                            <span className={`rounded px-2 ${topicType ? styles.labelTagTopic : lessonType ? styles.labelTagLesson : styles.labelTagAssessment}`}>
                                {topicType ? "topic" : (lessonType ? "lesson" : (assessmentType ? "assessment" : "pure inquiry"))}
                            </span>
                        </Col>
                    </Row>
                </ToolTip>
            </li>
        )
    }

    const handleOnSubmit = async () => {
        try {
            setSubmitting(true)
            if (periodName.trim().length === 0) {
                toast.error("Please enter a name for the grading period.")
                return
            }
            if (selectedAssignments.length === 0) {
                toast.error("Please select at least one due date to create a grading period.")
                return
            }

            await onSubmit(periodName, selectedAssignments, classId)
        } finally {
            setSubmitting(false)
        }
    }

    if (!show) return null
    if (assignments == null || (type === "edit" && !period)) return <BeatLoader />

  return (
    <Modal show={show} onHide={onHide} size="lg">
        <Modal.Header className="border-0" closeButton>
            <h3 className="w-100 text-center"> 
                <FontAwesomeIcon icon={type === "create" ? faBook : type === "edit" ? faPenToSquare : faGlasses} /> 
                <span className="ms-2">{type === "create" ? "Create" : type === "edit" ? "Edit" : "View"} Grading Period</span>
            </h3>
        </Modal.Header>
        <Modal.Body>
            {type === "view" && <p className="text-start">Grading Period: <span className="fw-bold">{period.name}</span></p>}
            <p className="text-start text-muted fw-bold">
                {type === "create" ? 
                    "Create a name for this Grading Period and select one or more assignments from your existing assignments list to define the time frame for this Grading Period." :
                    type === "edit" ? "Edit this Grading Period by updating the Grading Period name or the assignments to include in this Grading Period." : 
                    "The following assignments are part of this Grading Period."}
            </p>

            {(assignments.length === 0 && type === "create") && <h5>No assignments available to create grading periods.</h5>}
            {(assignments.length === 0 && (type === "edit" || type === "view")) && <h5>There are no assignments in this grading period.</h5>}
            {assignments.length > 0 && 
            <>
                {type !== "view" && 
                <Form className="text-start my-2">
                    <Form.Label className="fw-bold">{type === "edit" ? "Edit" : ""} Grading Period Name</Form.Label>
                    <Form.Control
                        name="gradingPeriodName"
                        type="text"
                        placeholder="Name"
                        value={periodName}
                        onChange={(e) => setPeriodName(e.target.value)}
                    />
                </Form>}
                {type !== "view" && <p className="text-start fw-bold">Select one or more assignments.</p>}
                <Paginator items={assignments} renderItem={renderPeriodItem} itemsPerPage={10} position="up" />
            </>
            }

            {type !== "view" && 
                <Button variant="primary" className={`w-50 mt-3`} onClick={handleOnSubmit} disabled={selectedAssignments.length === 0 || submitting}>
                    {type === "create" ? "Create" : "Update"} Grading Period
                </Button>}

            {type === "view" && <Button variant="primary" className={`w-50 mt-3`} onClick={onHide}>Return to selection</Button>}
        </Modal.Body>
    </Modal>
  )
}

export default GradingPeriodForm
